<template>
  <div :class="$style.page">
    <div :class="$style.banner">
      <picture :class="$style.banner__image">
        <source :srcset="require('@/assets/img/become-seller/banner_d.webp')" type="image/webp" media="(min-width: 768px)" />
        <source :srcset="require('@/assets/img/become-seller/banner_m.webp')" type="image/webp" />
        <img :class="$style.banner__image" :src="require('@/assets/img/become-seller/banner_m.webp')" alt="background" />
      </picture>

      <div :class="$style.banner__content">
        <h1 :class="$style.title">
          {{ $t("becomeSellerTitle") }}
        </h1>
        <p :class="$style.subtitle">
          {{ $t("becomeSellerSubtitle") }}
        </p>
        <Button is-primary :class="$style.start" @click="startSell">
          {{ $t("becomeSellerStart") }}
        </Button>
      </div>
    </div>

    <div :class="$style.page__body">
      <div :class="$style.block">
        <p :class="$style.blockTitle">
          {{ $t("becomeSellerOurAdvantages") }}
        </p>

        <div :class="$style.advantages">
          <div v-for="i in 7" :key="`point_${i}`" :class="$style.advantage">
            <div :class="$style.advantageIcon__container">
              <img :class="$style.advantageIcon" :src="pointIcon(i)" alt="icon" />
              <template v-if="i === 7">
                <span :class="$style.advantageIcon__splitter">{{ $t("or") }}</span>
                <img :class="$style.advantageIcon" :src="require('~/assets/img/icons/mail.svg')" alt="icon" />
              </template>
            </div>
            <p :class="$style.advantageText" v-html="$t(`becomeSellerOurAdvantagesPoint${i}`)" />
          </div>
        </div>
      </div>

      <div v-if="isRu" :class="[$style.block, $style.blockTwoColumn]">
        <div>
          <p :class="$style.blockTitle" v-html="$t('becomeSellerVideoTitle')" />

          <p :class="$style.advantageText" v-html="$t('becomeSellerWebinarText')" />

          <Button :class="$style.youtubeBtn" icon="youtube-white" is-primary @click="openYoutubeLink">
            {{ $t('watchOnYoutube') }}
          </Button>
        </div>
        <div :class="$style.videoBlock">
          <div :class="$style.video">
            <iframe width="100%" :src="youtubeLinkEmbed" />
          </div>
        </div>
      </div>

      <div :class="$style.block">
        <p :class="$style.blockTitle">
          {{ $t("becomeSellerRulesTitle") }}
        </p>

        <div :class="$style.rules">
          <div :class="[$style.rule, $style.ruleOrange]">
            <div :class="$style.ruleHead">
              <img :class="$style.ruleHead__icon" :src="require('~/assets/img/icons/bulb.svg')" alt="bulb" />
              <p :class="$style.ruleHead__title" v-html="$t('becomeSellerRule1Title')" />
            </div>

            <p :class="$style.rule__text" v-html="$t('becomeSellerRule1Text')" />
          </div>

          <div :class="[$style.rule, $style.ruleGreen]">
            <div :class="$style.ruleHead">
              <img :class="$style.ruleHead__icon" :src="require('~/assets/img/icons/wallet-header.svg')" alt="wallet" />
              <p :class="$style.ruleHead__title" v-html="$t('becomeSellerRule2Title')" />
            </div>

            <p :class="$style.rule__text" v-html="$t('becomeSellerRule2Text')" />
          </div>

          <div :class="[$style.rule, $style.ruleRed]">
            <div :class="$style.ruleHead">
              <img :class="$style.ruleHead__icon" :src="require('~/assets/img/icons/error-circle.svg')" alt="error" />
              <p :class="$style.ruleHead__title" v-html="$t('becomeSellerRule3Title')" />
            </div>

            <p :class="$style.rule__text" v-html="$t('becomeSellerRule3Text')" />
          </div>

          <div :class="$style.attention">
            <img :class="$style.attention__icon" :src="require('~/assets/img/icons/warning-2-red.svg')" alt="attention" />

            <div>
              <p :class="$style.attention__title">
                {{ $t("becomeSellerAttentionTitle") }}
              </p>
              <p :class="$style.attention__text">
                {{ $t("becomeSellerAttentionText") }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!isSeller" id="seller-form" :class="$style.blockGrey">
        <p :class="$style.blockTitle">
          {{ $t("becomeSellerFormTitle") }}
        </p>

        <template v-if="!isAuthorized">
          <p :class="$style.blockText">
            {{ $t("becomeSellerFormTextNonAuth") }}
          </p>

          <Button is-primary @click="openAuthModal">
            {{ $t("signUpAction") }}
          </Button>
        </template>

        <template v-else>
          <p :class="$style.formSubtitle">
            {{ $t("becomeSellerBusinessStatus") }}
          </p>
          <div :class="$style.radioSelection">
            <div v-for="sellerType in sellerTypes" :key="sellerType.id" :class="sellerTypeStyles(sellerType)" @click="setActiveSellerType(sellerType)">
              <div :class="roundStyles(sellerType)">
                <div v-if="isActiveSellerType(sellerType)" :class="$style.radioVariant__roundInside">
                </div>
              </div>
              <span :class="$style.radioVariant__title">{{ $t(sellerType.text) }}</span>
            </div>
          </div>

          <p :class="$style.formSubtitle">
            {{ $t("becomeSellerPersonalDetails") }}
          </p>

          <div :class="$style.radioSelection">
            <FormInput v-if="sellerTypeId === 'company'" v-model="companyName" :class="$style.field" :title="$t('companyName') + '*'" />
            <FormInput v-model="phoneNumber" :class="$style.field" :title="$t('phoneNumber') + '*'" />
            <FormInput :value="user?.email" :class="$style.field" :title="$t('email') + '*'" is-disabled />
            <FormInput v-model="vatNumber" :class="$style.field" :title="$t('vatNumber')" />
          </div>

          <p :class="$style.formSubtitle">
            {{ sellerTypeId === "company" ? $t("becomeSellerAddr") : $t("becomeSellerAddrIE") }}
          </p>

          <div :class="$style.radioSelection">
            <FormInput v-model="country" :class="$style.field" :title="$t('country')" />
            <FormInput v-model="city" :class="$style.field" :title="$t('city')" />
          </div>
          <div :class="$style.radioSelection">
            <FormInput v-model="postalCode" :class="$style.field" :title="$t('postalCode')" />
          </div>

          <p :class="$style.formSubtitle">
            {{ $t("notifications") }}
          </p>

          <div :class="$style.notification">
            <div :class="$style.notification__icon">
              <img :class="$style.notification__iconImg" :src="require('~/assets/img/icons/telegram-blue.svg')" alt="tg" />
            </div>
            <span>Telegram</span>
            <Button is-primary @click="openTgModal">
              {{ $t("buttonLink") }}
            </Button>
          </div>

          <div :class="$style.checkbox__container">
            <div :class="$style.checkbox">
              <CheckboxInput v-model="checkbox" />
              <span :class="$style.checkbox__label" v-html="$t('marketplaceCheckoutCheckboxText')"></span>
            </div>
          </div>

          <Button :class="$style.button" is-primary :is-disabled="buttonDisabled" @click="becomeSeller">
            {{ $t('becomeASeller') }}
          </Button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import FormInput from '~/components/FormInput/FormInput.vue'
import CheckboxInput from '~/components/CheckboxInput/CheckboxInput.vue'
import UsersService from '~/services/UsersService'
import { createMeta } from '~/utils/meta'

export default {
  components: {
    FormInput,
    CheckboxInput
  },

  data () {
    return {
      sellerTypeId: 'company',
      companyName: '',
      phoneNumber: '',
      vatNumber: '',
      country: '',
      city: '',
      postalCode: '',
      checkbox: false,
      youtubeLink: 'https://youtu.be/h0UE79iftrU',
      youtubeLinkEmbed: 'https://www.youtube.com/embed/h0UE79iftrU',
      icons: [
        require('~/assets/img/icons/sale.svg'),
        require('~/assets/img/icons/group.svg'),
        require('~/assets/img/icons/bitcoin.svg'),
        require('~/assets/img/icons/medal.svg'),
        require('~/assets/img/icons/chart.svg'),
        require('~/assets/img/icons/earn.svg'),
        require('~/assets/img/icons/telegram-blue.svg')
      ]
    }
  },

  computed: {
    ...mapState('user', ['user']),
    ...mapGetters('user', ['isAuthorized']),

    isRu () {
      return this.$i18n.locale === 'ru'
    },

    isSeller () {
      return this.user?.is_seller
    },

    sellerTypes () {
      return [
        {
          id: 'company',
          text: this.$t('company')
        },
        {
          id: 'individual',
          text: this.$t('soleProprietor')
        }
      ]
    },

    buttonDisabled () {
      return !this.checkbox || !this.phoneNumber?.length || (this.sellerTypeId === 'company' && !this.companyName?.length)
    }
  },

  methods: {
    ...mapActions('modal', ['showModal']),
    ...mapActions('user', ['fetchUser']),

    pointIcon (i) {
      return this.icons[i - 1]
    },

    openYoutubeLink () {
      window?.open(this.youtubeLink, '_blank')
    },

    openAuthModal () {
      this.showModal({
        component: 'Auth',
        data: {
          mode: 'register'
        }
      })
    },

    openTgModal () {
      this.showModal({
        component: 'LinkTelegram'
      })
    },

    openPhoneVerificationModal () {
      this.showModal({
        component: 'ModalVerification',
        data: {
          title: this.$t('errorCode19'),
          show18: false,
          onSuccess: async () => {
            await this.becomeSellerProcess()
            this.openTgModal()
          }
        }
      })
    },

    isActiveSellerType (variant) {
      return variant.id === this.sellerTypeId
    },

    sellerTypeStyles (variant) {
      return {
        [this.$style.radioVariant]: true,
        [this.$style.radioVariantActive]: this.isActiveSellerType(variant)
      }
    },

    roundStyles (variant) {
      return {
        [this.$style.radioVariant__round]: true,
        [this.$style.radioVariant__roundActive]: this.isActiveSellerType(variant)
      }
    },

    setActiveSellerType (variant) {
      this.sellerTypeId = variant.id
    },

    async becomeSellerProcess () {
      await UsersService.becomeSeller(this.$axios)
      try {
        window?.ym?.(96913300, 'reachGoal', 'saler')
      } catch (e) {
        console.log(e)
      }
      await this.fetchUser()
    },

    async sendForm () {
      await UsersService.sendSellerForm(this.$axios, {
        email: this.user?.email,
        phone: this.phone,
        seller_type: this.sellerTypeId,
        company_name: this.companyName,
        vat_number: this.vatNumber,
        country: this.country,
        city: this.city,
        postal_code: this.postalCode
      })
    },

    async becomeSeller () {
      if (this.user?.is_seller) {
        this.$router.push(this.localeLocation('/marketplace/add'))
        return
      }

      if (this.user?.verification_status === 'verified') {
        await this.sendForm()
        await this.becomeSellerProcess()
        this.$router.push(this.localeLocation('/marketplace/add'))
      } else {
        this.openPhoneVerificationModal()
      }
    },

    startSell () {
      if (!this.isAuthorized) {
        this.openAuthModal()
      } else if (!this.isSeller) {
        const element = window.document.getElementById('seller-form')
        element?.scrollIntoView({ behavior: 'smooth' })
      } else {
        this.$router.push(this.localeLocation('/marketplace/add'))
      }
    }
  },

  head () {
    if (this.$i18n.locale === 'ru') {
      const title = 'Продать игровые предметы, ключи, подписки — Gaming Goods'
      const description = 'У нас можно продать игру, игровую валюту, скины, внутриигровые предметы, донаты для Игр в стим, PS, Xbox, Nintendo и других. Продавайте игры и приложение на Gaming Goods, безопасно.'

      return createMeta.call(this, {
        title,
        description,
        sharingTitle: title,
        sharingDescription: description
      })
    }
  }
}
</script>

<style lang="scss" module>
.page {
  padding: 30px 0;
  width: 100%;
  color: $white;

  @media (min-width: $breakpointTablet) {
    padding: 0;
  }

  &__body {
    padding: 16px;
  }
}

.banner {
  position: relative;

  @media (min-width: $breakpointDesktopWide) {
    height: 400px;
  }

  &__image {
    display: block;
    width: 100%;
    height: 350px;
    object-fit: cover;

    @media (min-width: $breakpointDesktopWide) {
      position: absolute;
      top: 0;
      left: calc(-50vw + 50%);
      width: 100vw;
      height: 400px;
      max-height: 400px;
      overflow: hidden;
    }
  }

  &__content {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 282px;
    height: 100%;
    display: flex;
    justify-content: center;
    flex-direction: column;
    padding: 16px;

    @media (min-width: $breakpointTablet) {
      padding: 0 24px;
      width: 394px;
    }

    @media (min-width: $breakpointDesktopWide) {
      width: 664px;
      z-index: 2;
    }
  }
}

.title {
  font-size: 28px;
  font-weight: 700;
  line-height: 36px;
  margin-bottom: 12px;
}

.subtitle {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.start {
  margin-top: 24px;
}

.block {
  margin-top: 64px;

  &Grey {
    margin-top: 24px;
    padding: 24px 16px;
    background: $blue-grey70;
    border-radius: 24px;
  }

  &Title {
    font-size: 24px;
    font-weight: 700;
    line-height: 32px;
    margin: 0 0 24px;
  }

  &Text {
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
    margin: 0 0 32px;
  }

  &TwoColumn {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 24px;

    @media (min-width: $breakpointTablet) {
      grid-template-columns: 1fr 1fr;
    }

    @media (min-width: $breakpointDesktopWide) {
      width: 100%;
      grid-template-columns: 558px 1fr;
    }
  }
}

.youtubeBtn {
  margin-top: 32px;
  min-width: max-content;
  width: 100% !important;
  background: #f00 !important;

  @media (min-width: $breakpointTablet) {
    width: max-content !important;
  }
}

.advantages {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 16px;

  @media (min-width: $breakpointTablet) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: $breakpointDesktopWide) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
}

.advantage {
  display: grid;
  grid-template-columns: max-content 1fr;
  grid-gap: 12px;
  padding: 20px;
  height: 100%;
  background: $blue-grey70;
  border-radius: 16px;

  &:last-child {
    grid-template-columns: 1fr;
  }

  @media (min-width: $breakpointTablet) {
    align-items: center;

    &:last-child {
      grid-column: 1 / -1;
    }
  }

  @media (min-width: $breakpointDesktopWide) {
    &:last-child {
      grid-column: 3 / 5;
      grid-row: 2 / 4;
    }

    &:nth-child(5) {
      grid-column: 1 / 3;
    }

    &:nth-child(6) {
      grid-column: 1 / 3;
    }
  }

  &Icon {
    width: 24px;
    height: 24px;

    &__container {
      padding: 12px;
      width: max-content;
      height: max-content;
      border-radius: 50px;
      background: $blue-grey80;
    }

    &__splitter {
      color: $blue-grey30;
      font-size: 12px;
      font-weight: 500;
      line-height: 12px;
      text-transform: uppercase;
      margin: 0 8px;
    }
  }

  &Text {
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;

    li {
      padding-bottom: 4px;
    }

    ul, li {
      font-size: 16px;
      font-weight: 400;
      line-height: 20px;
      list-style-type: disc;
      list-style-position: inside;
      margin-left: 6px;
    }
  }
}

.videoBlock {
  width: 100%;
  margin-top: 24px;

  @media (min-width: $breakpointDesktopWide) {
    margin-top: 0;
  }
}

.video {
  border-radius: 16px;
  border: 4px solid $purple60-20;
  max-width: 558px;
  margin-left: auto;

  iframe {
    border-radius: 12px;
    min-height: 230px;

    @media (min-width: $breakpointDesktopWide) {
      min-height: 372px;
    }
  }
}

.rules {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 24px;

  @media (min-width: $breakpointDesktopWide) {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 24px;
  }
}

.attention {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 24px;
  color: $white;
  border-radius: 16px;
  padding: 24px 16px;
  border-right: 2px solid $pink60;
  border-left: 2px solid $pink60;
  background: $blue-grey90;

  @media (min-width: $breakpointTablet) {
    display: flex;
    gap: 24px;
    align-items: center;
  }

  @media (min-width: $breakpointDesktopWide) {
    grid-column: 1 / -1;
  }

  &__icon {
    padding: 12px;
    border-radius: 50%;
    height: 64px;
    width: 64px;
    background: $blue-grey70;
  }

  &__title {
    margin-bottom: 12px;
    font-size: 20px;
    font-weight: 700;
    line-height: 24px;
  }

  &__text {
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
  }
}

.rule {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: max-content 1fr;
  grid-gap: 24px;
  align-items: start;
  color: $white;
  border-radius: 16px;
  padding: 24px 16px;

  @media (min-width: $breakpointDesktopWide) {
    padding: 24px;
  }

  &Orange {
    border-top: 2px solid $orange60;
    background: linear-gradient(180deg, rgba(251, 140, 0, 0.10) 0%, rgba(0, 0, 0, 0.20) 100%);
  }

  &Red {
    border-top: 2px solid $red60;
    background: linear-gradient(180deg, rgba(236, 43, 45, 0.10) 0%, rgba(0, 0, 0, 0.20) 100%);
  }

  &Green {
    border-top: 2px solid $green60;
    background: linear-gradient(180deg, rgba(251, 140, 0, 0.10) 0%, rgba(0, 0, 0, 0.20) 100%);
  }

  &Head {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 16px;

    &__icon {
      width: 40px;
      height: 40px;
    }

    &__title {
      font-size: 20px;
      font-weight: 700;
      line-height: 24px;
    }
  }

  &__text {
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;

    li {
      padding-bottom: 8px;
    }

    ul, li {
      margin-left: 6px;
      font-size: 16px;
      font-weight: 400;
      line-height: 20px;
      list-style-type: disc;
      list-style-position: inside;
    }
  }
}

.radioSelection {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 8px;

  @media (min-width: $breakpointTablet) {
    grid-template-columns: 1fr 1fr;
  }
}

.radioVariant {
  display: grid;
  grid-template-columns: 20px 1fr;
  grid-gap: 12px;
  align-items: center;
  padding: 12px;
  background: $blue-grey60;
  border: 1px solid $blue-grey60;
  border-radius: 8px;
  cursor: pointer;

  &Active {
    border: 1px solid $primary60;
  }

  &__icon {
    width: 24px;
    height: 24px;
  }

  &__title {
    color: $white;
    font-size: 14px;
    font-weight: 500;
    line-height: 120%;
  }

  &__round {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid $blue-grey20;

    &Active {
      border: 2px solid $primary60;
    }

    &Inside {
      width: 100%;
      height: 100%;
      background: $primary60;
      border: 2px solid $blue-grey60;
      border-radius: 50%;
    }
  }
}

.formSubtitle {
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  margin: 40px 0 24px;
}

.field {
  margin-top: 12px;
}

.notification {
  display: grid;
  grid-template-columns: 56px 1fr max-content;
  grid-gap: 24px;
  align-items: center;
  padding: 0 24px;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  background: $blue-grey60;
  border-radius: 16px;

  &__icon {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: 72px;
    width: 56px;
    border-right: 1px solid $blue-grey40;

    &Img {
      height: 24px;
      width: 24px;
    }
  }
}

.checkbox {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  margin: 16px 0;
  color: $white;

  &__label {
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
  }

  &__container {
    margin: 40px 0;
    border-top: 1px solid $blue-grey40;
    border-bottom: 1px solid $blue-grey40;
  }
}
</style>
